<template>
  <div
    class="autocomplete"
    ref="$el"
  >
    <input
      ref="inputEl"
      v-model="search"
      @focus="onFocus"
      @input="onChange"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter="onEnter"
      class="autocomplete-input"
      placeholder="New tag to add"
      type="text"
    >
    <Teleport
      to="body"
    >
      <ul
        v-show="isOpen"
        ref="dropdownEl"
        class="autocomplete-results"
        :style="dropdownStyle"
      >
        <li
          v-if="isLoading"
          class="loading"
        >
          Loading results...
        </li>
        <li
          v-else
          v-for="(result, i) in results"
          :key="`${result.value}-${result.meta ?? ''}-${i}`"
          @click="setResult(result)"
          @dblclick="setResultAndSubmit(result)"
          class="autocomplete-result"
          :class="{ 'is-active': i === arrowCounter }"
        >
          <div
            class="autocomplete-result-text"
          >
            <span
              class="autocomplete-result-value"
            >{{ result.value }}</span>
            <span
              v-if="result.meta"
              class="autocomplete-result-meta"
            >{{ result.meta }}</span>
          </div>
          <button
            v-if="result.canToggleFavorite"
            type="button"
            class="autocomplete-result-action"
            :class="{ 'is-favorite': result.isFavorite }"
            :title="result.isFavorite ? 'Remove bookmark' : 'Bookmark tag'"
            :aria-label="result.isFavorite ? 'Remove bookmark' : 'Bookmark tag'"
            @click.stop="toggleFavorite(result)"
          >
            <Bookmark
              :size="14"
              :fill="result.isFavorite ? 'currentColor' : 'none'"
            />
          </button>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  defineEmits,
  defineModel,
  defineOptions,
  defineProps,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  withDefaults,
} from "vue";
import { Bookmark } from "lucide-vue-next";
import type { TagAutocompleteItem } from "@/components/tagAutocompletion";

defineOptions({
  name: "TagAutocompletion",
});

type Props = {
  items: TagAutocompleteItem[];
  isAsync?: boolean;
};

const props: Required<Props> = withDefaults(defineProps<Props>(), {
  items: () => [],
  isAsync: false,
});

const emit = defineEmits<{
  submit: [value: string];
  select: [value: string];
  "focus-empty": [];
  "toggle-favorite": [value: string];
  "update:modelValue": [value: string];
}>();

const search = defineModel<string>({ default: "" });
const isOpen = ref(false);
const isLoading = ref(false);
const results = ref<TagAutocompleteItem[]>([]);
const arrowCounter = ref(-1);
const hasFocus = ref(false);
const $el = ref<HTMLElement>();
const inputEl = ref<HTMLInputElement>();
const dropdownEl = ref<HTMLUListElement>();
const dropdownStyle = ref<Record<string, string>>({});

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleViewportChange);
  window.removeEventListener("scroll", handleViewportChange, true);
});

watch(
  () => props.items,
  (newValue) => {
    if (props.isAsync) {
      results.value = newValue.slice(0, 50);
      isLoading.value = false;
      isOpen.value = hasFocus.value && (search.value !== "" || results.value.length > 0);
      if (isOpen.value) {
        nextTick(() => positionDropdown());
      }
      return;
    }

    filterResults();
  },
  { immediate: true, deep: true },
);

watch(search, (newValue) => {
  if (newValue === "" && hasFocus.value) {
    emit("focus-empty");
  }
  if (isOpen.value) {
    nextTick(() => positionDropdown());
  }
});

watch(isOpen, (open) => {
  if (open) {
    nextTick(() => positionDropdown());
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return;
  }

  window.removeEventListener("resize", handleViewportChange);
  window.removeEventListener("scroll", handleViewportChange, true);
});

function filterResults() {
  const normalizedSearch = search.value.toLowerCase();
  results.value = props.items
    .slice(0, 50)
    .filter((item) => item.value.toLowerCase().includes(normalizedSearch));
}

function onFocus() {
  hasFocus.value = true;
  if (search.value === "") {
    emit("focus-empty");
  }
  if (!props.isAsync) {
    filterResults();
  }
  isOpen.value = results.value.length > 0 || search.value !== "";
  if (isOpen.value) {
    nextTick(() => positionDropdown());
  }
}

function onChange() {
  if (props.isAsync) {
    if (search.value === "") {
      isLoading.value = false;
      emit("focus-empty");
      isOpen.value = props.items.length > 0;
      if (isOpen.value) {
        nextTick(() => positionDropdown());
      }
      return;
    }

    isLoading.value = true;
    isOpen.value = true;
    nextTick(() => positionDropdown());
    return;
  }

  filterResults();
  isOpen.value = true;
  nextTick(() => positionDropdown());
}

function close() {
  arrowCounter.value = -1;
  isLoading.value = false;
  isOpen.value = false;
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node | null;
  if (!target) return;
  if ($el.value?.contains(target)) return;
  if (dropdownEl.value?.contains(target)) return;
  hasFocus.value = false;
  close();
}

function onArrowDown() {
  if (arrowCounter.value < results.value.length - 1) {
    arrowCounter.value += 1;
  }
}

function onArrowUp() {
  if (arrowCounter.value > 0) {
    arrowCounter.value -= 1;
  }
}

function onEnter() {
  if (isOpen.value && arrowCounter.value !== -1) {
    select(results.value[arrowCounter.value].value);
    return;
  }

  if (
    search.value !== "" &&
    ((isOpen.value && results.value.length === 0) || !isOpen.value)
  ) {
    emit("submit", search.value);
  }
}

function setResult(result: TagAutocompleteItem) {
  select(result.value);
}

function setResultAndSubmit(result: TagAutocompleteItem) {
  setResult(result);
  emit("submit", result.value);
}

function select(value: string) {
  search.value = value;
  close();
  emit("select", value);
  emit("update:modelValue", value);
}

function toggleFavorite(result: TagAutocompleteItem) {
  emit("toggle-favorite", result.value);
}

function positionDropdown() {
  if (!isOpen.value || inputEl.value === undefined) return;

  const rect = inputEl.value.getBoundingClientRect();
  const margin = 8;
  const viewportPadding = 12;
  const preferredHeight = Math.min(320, Math.max(window.innerHeight * 0.45, 160));
  const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
  const spaceAbove = rect.top - viewportPadding;
  const openUpward = spaceAbove > spaceBelow && spaceAbove >= 120;
  const maxHeight = Math.max(
    120,
    Math.min(preferredHeight, openUpward ? spaceAbove - margin : spaceBelow - margin),
  );
  const width = Math.min(rect.width, window.innerWidth - viewportPadding * 2);
  const left = Math.min(
    Math.max(viewportPadding, rect.left),
    window.innerWidth - width - viewportPadding,
  );

  dropdownStyle.value = {
    position: "fixed",
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: "2500",
    top: openUpward ? "auto" : `${rect.bottom + margin}px`,
    bottom: openUpward ? `${window.innerHeight - rect.top + margin}px` : "auto",
  };
}

function handleViewportChange() {
  if (isOpen.value) {
    positionDropdown();
  }
}
</script>

<style scoped>
.autocomplete {
  position: relative;
}

.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  border-radius: 0.5rem;
  background: var(--bg-surface, #1e1e1e);
  overflow-y: auto;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.5rem;

  &.is-active {
    background-color: #4aae9b;
    color: white;
  }

  &:hover {
    background-color: #4a57ae;
    color: white;
  }
}

.autocomplete-result-text {
  min-width: 0;
  flex: 1 1 auto;
  padding: 0.55rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.autocomplete-result-value {
  word-break: break-word;
}

.autocomplete-result-meta {
  font-size: 0.78em;
  opacity: 0.7;
}

.autocomplete-result-action {
  flex: 0 0 auto;
  min-width: 2.5rem;
  border: none;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.7rem;
  cursor: pointer;

  &.is-favorite {
    color: #ffd166;
  }
}
</style>
